import { useState } from 'react';

import {
  InfiniteData,
  QueryKey,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';
import { useSetAtom } from 'jotai/react';
import { useTranslation } from 'next-i18next';

import { DTOFavoriteRequest } from 'src/apis/dto/DTOFavoriteRequest';
import {
  DTOFollowInfo,
  DTOFollowInfoState,
  DTO_FOLLOW_INFO_STATE_TYPES,
} from 'src/apis/dto/DTOFollowInfo';
import { DTOFollowPageResponse } from 'src/apis/dto/DTOFollowPageResponse';
import type { DTOFollowRequest } from 'src/apis/dto/DTOFollowRequest';
import { DTOProfileCard } from 'src/apis/dto/DTOProfileCard';
import { REST_ERROR_CODES } from 'src/apis/exception';
import { RESTClient } from 'src/apis/service';
import {
  FollowerListFilter,
  FollowingListFilter,
  FollowService,
} from 'src/apis/service/FollowService';
import { isDefinedAxiosRESTFailure } from 'src/apis/utils';
import useLogin from 'src/hooks/useLogin';
import { ProfileQueryKeys } from 'src/hooks/useProfileQuery';
import { showCommonErrorToastAtom, showToastAtom } from 'src/stores/toast/atoms';
import { MatchInfo } from 'src/types/Match';
import { ToastType } from 'src/types/Toast';
import { optimisticUpdaterFactory } from 'src/utils/query';

export const FOLLOW_QUERY_KEYS = {
  // follow
  follow: () => ['follow'],
  followings: (filter?: FollowingListFilter) =>
    [...FOLLOW_QUERY_KEYS.follow(), 'following', filter].filter((v): v is string => Boolean(v)),
  followers: (filter?: FollowerListFilter) =>
    [...FOLLOW_QUERY_KEYS.follow(), 'follower', filter].filter((v): v is string => Boolean(v)),

  dmLobbyFollowings: () => [...FOLLOW_QUERY_KEYS.follow(), 'dmLobbyFollowings'],
  dmLobbyFollowingCount: () => [...FOLLOW_QUERY_KEYS.follow(), 'dmLobbyFollowingCount'],
};

export function useFollowingsQuery(
  filter: FollowingListFilter = 'ALL',
  options?: Pick<
    UseInfiniteQueryOptions<
      DTOFollowPageResponse,
      unknown,
      InfiniteData<DTOFollowPageResponse>,
      QueryKey,
      DTOFollowPageResponse['cursor'] | null
    >,
    'refetchOnMount'
  >
) {
  const [service] = useState(() => new FollowService(new RESTClient()));
  const { isLogin } = useLogin();

  return useInfiniteQuery<
    DTOFollowPageResponse,
    unknown,
    InfiniteData<DTOFollowPageResponse>,
    QueryKey,
    DTOFollowPageResponse['cursor'] | null
  >({
    queryKey: FOLLOW_QUERY_KEYS.followings(filter),
    async queryFn({ pageParam }) {
      const cursor = pageParam ?? undefined;

      return service.getFollowingsWithFilter(filter, {
        cursor,
        size: 10,
      });
    },
    enabled: isLogin,
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.cursor ?? null,
    ...options,
  });
}

export function useFollowersQuery(
  filter: FollowerListFilter = 'ALL',
  options?: Pick<
    UseInfiniteQueryOptions<
      DTOFollowPageResponse,
      unknown,
      InfiniteData<DTOFollowPageResponse>,
      QueryKey,
      DTOFollowPageResponse['cursor'] | null
    >,
    'refetchOnMount'
  >
) {
  const { isLogin } = useLogin();

  return useInfiniteQuery<
    DTOFollowPageResponse,
    unknown,
    InfiniteData<DTOFollowPageResponse>,
    QueryKey,
    DTOFollowPageResponse['cursor'] | null
  >({
    queryKey: FOLLOW_QUERY_KEYS.followers(filter),
    async queryFn({ pageParam }) {
      const cursor = pageParam ?? undefined;

      return new FollowService(new RESTClient()).getFollowersWithFilter(filter, {
        cursor,
        size: 10,
      });
    },
    enabled: isLogin,
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.cursor ?? null,
    ...options,
  });
}

const toggleFollowState = (id: DTOFollowInfo['followId'], newState: DTOFollowInfoState) =>
  optimisticUpdaterFactory<InfiniteData<DTOFollowPageResponse>>((prev) => {
    for (const pageIdx in prev.pages) {
      const followIdx = prev.pages[pageIdx].follows.findIndex(({ followId }) => followId === id);
      if (followIdx >= 0) {
        prev.pages[pageIdx].follows[followIdx].state = newState;
        break;
      }
    }
  });

/** 언팔로우 후 서버 기준으로 팔로잉 목록에서 빠지므로, `state`만 바꾸지 않고 항목을 제거한다. */
const removeFollowingFromPages = (id: DTOFollowInfo['followId']) =>
  optimisticUpdaterFactory<InfiniteData<DTOFollowPageResponse>>((prev) => {
    for (const pageIdx in prev.pages) {
      prev.pages[pageIdx].follows = prev.pages[pageIdx].follows.filter(
        ({ followId }) => followId !== id
      );
    }
  });

export const useIsFollowState = (followInfo: Pick<DTOFollowInfo, 'state'>) => {
  return [DTO_FOLLOW_INFO_STATE_TYPES.ACCEPTED, DTO_FOLLOW_INFO_STATE_TYPES.REQUESTED_BY_USER].some(
    (state) => state === followInfo.state
  );
};

export function useFollowerToggleFollowMutation(
  followInfo: DTOFollowInfo,
  filter: FollowerListFilter,
  options?: Omit<UseMutationOptions<void, unknown, void, unknown>, 'mutationFn'>
) {
  const service = new FollowService(new RESTClient());

  const { followId: id } = followInfo;

  const queryClient = useQueryClient();

  const isFollowing = useIsFollowState(followInfo);

  return useMutation({
    async mutationFn() {
      const payload: DTOFollowRequest = {
        idType: 'FOLLOWER',
        id,
        path: 'FOLLOWER',
      };

      if (isFollowing) {
        return service.unfollow(payload);
      } else {
        return service.follow(payload);
      }
    },
    ...options,
    async onSuccess(...args) {
      const newState = isFollowing ? 'NONE' : 'ACCEPTED';

      queryClient.setQueryData(
        FOLLOW_QUERY_KEYS.followers(filter),
        toggleFollowState(id, newState)
      );
      queryClient.invalidateQueries({ queryKey: FOLLOW_QUERY_KEYS.followings() });
      queryClient.invalidateQueries({ queryKey: FOLLOW_QUERY_KEYS.dmLobbyFollowings() });
      queryClient.invalidateQueries({ queryKey: FOLLOW_QUERY_KEYS.dmLobbyFollowingCount() });

      options?.onSuccess?.(...args);
    },
  });
}

export function useFollowingToggleFollowMutation(
  followInfo: DTOFollowInfo,
  filter: FollowingListFilter,
  options?: Omit<UseMutationOptions<void, unknown, 'follow' | 'unfollow', unknown>, 'mutationFn'>
) {
  const { followId: id } = followInfo;

  const queryClient = useQueryClient();

  const isFollowing = useIsFollowState(followInfo);

  return useMutation({
    async mutationFn() {
      const service = new FollowService(new RESTClient());

      const payload: DTOFollowRequest = {
        idType: 'FOLLOWING',
        id,
        path: 'FOLLOWING',
      };

      if (isFollowing) {
        return service.unfollow(payload);
      } else {
        return service.follow(payload);
      }
    },
    ...options,
    async onSuccess(...args) {
      const newState = isFollowing ? 'NONE' : 'ACCEPTED';

      queryClient.setQueriesData<InfiniteData<DTOFollowPageResponse>>(
        { queryKey: FOLLOW_QUERY_KEYS.followings() },
        newState === 'NONE' ? removeFollowingFromPages(id) : toggleFollowState(id, newState)
      );
      queryClient.invalidateQueries({ queryKey: FOLLOW_QUERY_KEYS.followings() });
      queryClient.invalidateQueries({ queryKey: FOLLOW_QUERY_KEYS.followers() });
      queryClient.invalidateQueries({
        queryKey: FOLLOW_QUERY_KEYS.dmLobbyFollowings(),
        refetchType: 'all',
      });
      queryClient.invalidateQueries({
        queryKey: FOLLOW_QUERY_KEYS.dmLobbyFollowingCount(),
        refetchType: 'all',
      });

      options?.onSuccess?.(...args);
    },
  });
}

export function useProfileToggleFollowMutation(
  followInfo: Pick<DTOProfileCard, 'id' | 'type' | 'followState'>,
  followRequest: Omit<DTOFollowRequest, 'id'>,
  /**
   * 도메인별 후속 캐시 동기화 등을 얹을 수 있는 확장 지점.
   * 내부 공용 sync(ProfileCard / FOLLOW 캐시) 가 끝난 뒤 호출된다.
   * 토글 결과로 결정된 `nextFollowState` 가 인자로 전달되므로 호출부는 재계산이 불필요.
   * 예: History 도메인은 `useHistoryProfileToggleFollowMutation` 에서 주입.
   */
  options?: { onSuccess?: (nextFollowState: string | undefined) => void | Promise<void> }
) {
  const { id, type } = followInfo;

  const { t } = useTranslation();

  const queryClient = useQueryClient();

  const showToast = useSetAtom(showToastAtom);
  const showCommonErrorToast = useSetAtom(showCommonErrorToastAtom);

  const isFollowing = useIsFollowState({ state: followInfo.followState });

  return useMutation({
    async mutationFn() {
      const service = new FollowService(new RESTClient());

      const payload: DTOFollowRequest = {
        ...followRequest,
        id,
      };

      try {
        if (isFollowing) {
          return await service.unfollow(payload);
        } else {
          return await service.follow(payload);
        }
      } catch (error) {
        // 서버 성능을 위해 팔로우 정보를 미리 불러오지 않아 요청을 보낸 후 갱신 처리
        if (isDefinedAxiosRESTFailure(error, REST_ERROR_CODES.ALREADY_FOLLOWED)) {
          showToast({
            type: ToastType.ERROR,
            message: t('follow_error_following'),
          });

          queryClient.setQueryData<DTOProfileCard>(
            ProfileQueryKeys.getProfileCard(type, id),
            optimisticUpdaterFactory<DTOProfileCard>((prev) => {
              prev.followState = 'ACCEPTED';
            })
          );

          return;
        }

        throw error;
      }
    },
    async onSuccess() {
      const followState = isFollowing ? undefined : 'ACCEPTED';

      queryClient.setQueryData<DTOProfileCard>(
        ProfileQueryKeys.getProfileCard(type, id),
        optimisticUpdaterFactory<DTOProfileCard>((prev) => {
          prev.followState = followState;
        })
      );
      queryClient.invalidateQueries({
        queryKey: ProfileQueryKeys.getProfileCard(),
        refetchType: 'inactive',
      });
      queryClient.invalidateQueries({
        queryKey: FOLLOW_QUERY_KEYS.follow(),
        refetchType: 'all',
      });

      await options?.onSuccess?.(followState);
    },
    onError() {
      showCommonErrorToast();
    },
  });
}

export const useProfileToggleFavoriteMutation = (
  { id }: DTOProfileCard,
  favoriteRequest: Pick<DTOFavoriteRequest, 'idType' | 'path'>,
  options?: Omit<UseMutationOptions<void, unknown, boolean, unknown>, 'mutationFn'>
) => {
  const service = new FollowService(new RESTClient());

  const queryClient = useQueryClient();

  const showCommonErrorToast = useSetAtom(showCommonErrorToastAtom);

  return useMutation({
    async mutationFn(isFavorite: boolean) {
      return service.changeFavorite({
        ...favoriteRequest,
        id,
        isFavorite,
      });
    },
    ...options,
    async onSuccess(...args) {
      await queryClient.invalidateQueries({
        queryKey: FOLLOW_QUERY_KEYS.follow(),
        refetchType: 'all',
      });

      await options?.onSuccess?.(...args);
    },
    async onError(...args) {
      showCommonErrorToast();

      await options?.onError?.(...args);
    },
  });
};

export const useMatchFollowMutation = (
  { matchId }: MatchInfo,
  options?: Omit<UseMutationOptions<void, unknown, void, unknown>, 'mutationFn'>
) => {
  const { t } = useTranslation();

  const showToast = useSetAtom(showToastAtom);

  const queryClient = useQueryClient();

  return useMutation<void, unknown, void, unknown>({
    async mutationFn() {
      const service = new FollowService(new RESTClient());

      try {
        await service.follow({
          id: matchId,
          idType: 'MATCH',
          path: 'IN_MATCH',
        });
      } catch (error) {
        /** 이미 팔로우하고 있는 경우 경고만 하고 넘어갑니다. */
        if (isDefinedAxiosRESTFailure(error, REST_ERROR_CODES.ALREADY_FOLLOWED)) {
          showToast({
            type: ToastType.ERROR,
            message: t('follow_error_following'),
          });
          return;
        }

        throw error;
      }
    },
    ...options,
    onSuccess(...args) {
      queryClient.invalidateQueries({
        queryKey: ProfileQueryKeys.getProfileCard(),
        refetchType: 'inactive',
      });
      queryClient.invalidateQueries({
        queryKey: FOLLOW_QUERY_KEYS.follow(),
        refetchType: 'inactive',
      });
      options?.onSuccess?.(...args);
    },
  });
};
